import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";
import {
  interleaveTranscriptInfo,
  transcriptArray as makeTranscriptArray,
  transcriptString,
  InterleavedTranscriptItem,
  TranscriptPartLike,
  TranscriptChannelParts,
} from "@/store/audio-capture/transcription/utils/transcript-interleaver";
import type { TranscriptionCoordinator } from "@/store/audio-capture/transcription/services/transcription-coordinator";
import type { TranscriptionChannelManager } from "@/store/audio-capture/transcription/interfaces";

export class TranscriptionFacade {
  constructor(private readonly coordinator: TranscriptionCoordinator) {}

  initializeTranscriptions(args?: { clearTranscript?: boolean }): void {
    this.coordinator.initializeTranscriptions(args ?? {});
  }

  start(): void {
    this.coordinator.startTranscriptions();
  }

  stop(): void {
    this.coordinator.stopTranscriptions();
  }

  pause(): void {
    this.coordinator.pauseTranscriptions();
  }

  resume(): void {
    this.coordinator.resumeTranscriptions();
  }

  reset(args?: { clearTranscript?: boolean }): void {
    this.coordinator.resetTranscriptions(args ?? {});
  }

  flushBuffers(): void {
    this.coordinator.flushAudioBuffers();
  }

  initializeSockets(): void {
    this.coordinator.initializeWebSockets();
  }

  async cleanupSockets(): Promise<void> {
    await this.coordinator.cleanupWebSocketAndAudio();
  }

  interleavedTranscript(managers: TranscriptionChannelManager[]): InterleavedTranscriptItem[] {
    const channels = managers.map(manager => ({
      channel: manager.channel,
      parts: manager.allTranscriptedParts as (
        | TimestampedAssemblyAITurnMessage[]
        | TranscriptPartLike
      )[],
    })) as TranscriptChannelParts[];
    return interleaveTranscriptInfo(channels);
  }

  transcriptString(managers: TranscriptionChannelManager[]): string {
    return transcriptString(this.interleavedTranscript(managers));
  }

  transcriptArray(managers: TranscriptionChannelManager[]) {
    return makeTranscriptArray(this.interleavedTranscript(managers));
  }
}
